import { HttpErrorResponse, HttpParams, HttpRequest } from '@angular/common/http';
import { UiErrorParser } from '../ui-error-parser';
import { Inject, Injectable, InjectionToken, Optional, signal } from '@angular/core';
import { operationsText } from '../../../app/shared/utils/http-texts';

export type IgnoreMeta = {
    path: string;
    method?: keyof typeof operationsText;
    message?: string;
    status?: number;
    args?: HttpParams;
};

export const IGNORE_ERRORS = new InjectionToken<((im: IgnoreMeta) => boolean)[]>('IGNORE_ERRORS');

/**
 * A janela em que uma operação EM LOTE assume, ela mesma, o relato dos erros dos seus itens.
 *
 * Existe porque o toast global é por resposta HTTP: uma fila com dez falhas empilha dez toasts sobre a
 * tela que já lista as dez, uma a uma, com o erro real de cada uma. Suprimir por path o ano inteiro
 * mudaria o fluxo UNITÁRIO dos mesmos endpoints, onde o toast é a única notícia que o analista tem —
 * daí a janela: fora dela nada muda.
 *
 * Raiz (`providedIn: 'root'`) porque quem a consulta é um matcher do `IGNORE_ERRORS`, montado no
 * injetor raiz, e quem a abre é um componente de feature.
 */
@Injectable({ providedIn: 'root' })
export class SilenciadorDeErrosEmLote {
    private readonly emCursoSignal = signal(false);
    /** Há uma operação em lote em curso agora. */
    readonly emCurso = this.emCursoSignal.asReadonly();

    iniciar(): void {
        this.emCursoSignal.set(true);
    }

    encerrar(): void {
        this.emCursoSignal.set(false);
    }
}

/**
 * Matcher do `IGNORE_ERRORS`: cala o toast global só quando as DUAS coisas valem — a janela do lote
 * está aberta E o path é de um endpoint que o lote usa. Os prefixos vêm de fora (`app.config.ts`, onde
 * moram os demais matchers e o `SICOM_PATHS`) para este arquivo não virar uma segunda lista de
 * endereços de endpoint.
 */
export function ignorarErrosDeOperacaoEmLote(silenciador: SilenciadorDeErrosEmLote, prefixos: readonly string[]): (im: IgnoreMeta) => boolean {
    return (im: IgnoreMeta) => silenciador.emCurso() && prefixos.some((prefixo) => im.path.startsWith(prefixo));
}

@Injectable()
export class IgnoreErrorParser extends UiErrorParser {
    ignoreMatchers: ((im: IgnoreMeta) => boolean)[] = [];

    constructor(
        @Optional()
        @Inject(IGNORE_ERRORS)
        ignoredErrors: ((im: IgnoreMeta) => boolean)[]
    ) {
        super();
        if (ignoredErrors) this.ignoreMatchers = ignoredErrors;
    }

    verifyContext(error: HttpErrorResponse, req: HttpRequest<unknown>): boolean {
        const meta = this.getIgnoreMeta(req, error);
        for (const matcher of this.ignoreMatchers) {
            if (matcher && matcher(meta)) {
                return true;
            }
        }
        return false;
    }

    override errorComplement(_error: HttpErrorResponse, _req: HttpRequest<unknown>): string {
        return '';
    }

    override beforeTextBuild(_error: HttpErrorResponse, _req: HttpRequest<unknown>): void {
        this.setIgnore(true);
    }

    private getIgnoreMeta(req: HttpRequest<unknown>, error: HttpErrorResponse): IgnoreMeta {
        return {
            path: req.url,
            method: req.method as keyof typeof operationsText,
            args: req.params,
            message: error?.error?.message ?? error?.message ?? undefined,
            status: error?.status ?? undefined
        };
    }
}
